import { Link, type LucideIcon } from 'lucide-react';
import { MODAL_NAMES } from '../store/modal.store';
import { WebsiteModalTypeEnum } from '@/Domain/enum/WebsiteModalType.enum';

export const UploadUrlModalHelper = {
  validateUrl: (input: string): boolean => {
    // Reject internal whitespace before delegating to the WHATWG URL parser:
    // the parser is lenient about pre-encoded spaces inside the hostname and
    // path (e.g. `https://example.com. some words` is happily accepted as
    // `example.com.` + a path of `/%20some%20words`), which surfaces backend-
    // side as a confusing Zod-style "Invalid URL" error after the user has
    // already submitted. Catch it here so the user sees the inline red border
    // before the request goes out.
    if (/\s/.test(input)) {
      return false;
    }
    // Mirror the backend's strict urlparse: WHATWG `new URL()` silently
    // normalizes `https:///foo.com` and reports hostname `foo.com`, but
    // Python's `urllib.parse.urlparse` returns netloc='' and the backend
    // rejects it (onboard-from-url returned a 500 in prod for one such paste,
    // trace 86ae92da1edaae7ac9e977f92b3a80da). Reject the malformed
    // scheme-with-extra-slashes here so the user sees the inline red border
    // before the request goes out.
    if (/:\/\/\//.test(input)) {
      return false;
    }
    try {
      const url = new URL(input);
      const hostname = url.hostname.replace(/\.+$/, '');
      // WHATWG `new URL()` keeps characters that are never valid in a domain
      // label (e.g. the apostrophe in `mcdonald's.com`) in the hostname rather
      // than throwing, so they sail past the parser and DNS-fail on the
      // backend. Reject any hostname character outside the set valid for a
      // domain: unicode letters/digits (for IDN, which the parser punycodes),
      // plus the `.` and `-` separators.
      if (/[^\p{L}\p{N}.-]/u.test(hostname)) {
        return false;
      }
      const domainParts = hostname.split('.');
      return domainParts.length >= 2 && domainParts[domainParts.length - 1].length >= 2;
    } catch {
      return false;
    }
  },

  parseMultipleUrls: (input: string): string[] =>
    input
      .split(/[\s,;]+/)
      .map((u) => u.trim())
      .filter(Boolean),

  formatUrl: (url: string): string => url,

  getModalIdByType: (type: WebsiteModalTypeEnum): string => {
    switch (type) {
      case WebsiteModalTypeEnum.SINGLE:
        return MODAL_NAMES.UPLOAD_URL_MODAL_ID;
      case WebsiteModalTypeEnum.MULTIPLE:
        return MODAL_NAMES.UPLOAD_MULTIPLE_URLS_MODAL_ID;
      case WebsiteModalTypeEnum.SITEMAP:
        return MODAL_NAMES.UPLOAD_SITEMAP_MODAL_ID;
      case WebsiteModalTypeEnum.ALL_PAGES:
        return MODAL_NAMES.UPLOAD_ALL_PAGES_MODAL_ID;
      case WebsiteModalTypeEnum.YOUTUBE:
        return MODAL_NAMES.UPLOAD_YOUTUBE_MODAL_ID;
    }
  },

  isSingleUrlType: (type: WebsiteModalTypeEnum): boolean =>
    type === WebsiteModalTypeEnum.SINGLE ||
    type === WebsiteModalTypeEnum.SITEMAP ||
    type === WebsiteModalTypeEnum.ALL_PAGES,

  getTitle: (type: WebsiteModalTypeEnum, t: any): string => {
    switch (type) {
      case WebsiteModalTypeEnum.SINGLE:
        return t('modals.upload_url.single_page.title');
      case WebsiteModalTypeEnum.YOUTUBE:
        return t('modals.upload_video.youtube.title');
      case WebsiteModalTypeEnum.MULTIPLE:
        return t('modals.upload_url.multiple_pages.title');
      case WebsiteModalTypeEnum.SITEMAP:
        return t('modals.upload_url.sitemap.title');
      case WebsiteModalTypeEnum.ALL_PAGES:
        return t('modals.upload_url.all_pages.title');
      default:
        return '';
    }
  },

  getDescription: (type: WebsiteModalTypeEnum, t: any): string => {
    switch (type) {
      case WebsiteModalTypeEnum.SINGLE:
        return t('modals.upload_url.single_page.description');
      case WebsiteModalTypeEnum.YOUTUBE:
        return t('modals.upload_video.youtube.description');
      case WebsiteModalTypeEnum.MULTIPLE:
        return t('modals.upload_url.multiple_pages.description');
      case WebsiteModalTypeEnum.SITEMAP:
        return t('modals.upload_url.sitemap.description');
      case WebsiteModalTypeEnum.ALL_PAGES:
        return t('modals.upload_url.all_pages.description');
      default:
        return '';
    }
  },

  getUrlLabel: (type: WebsiteModalTypeEnum, t: any): string => {
    switch (type) {
      case WebsiteModalTypeEnum.SINGLE:
        return t('modals.upload_url.single_page.url_label');
      case WebsiteModalTypeEnum.YOUTUBE:
        return t('modals.upload_video.youtube.url_label');
      case WebsiteModalTypeEnum.MULTIPLE:
        return t('modals.upload_url.multiple_pages.url_label');
      case WebsiteModalTypeEnum.SITEMAP:
        return t('modals.upload_url.sitemap.url_label');
      case WebsiteModalTypeEnum.ALL_PAGES:
        return t('modals.upload_url.all_pages.url_label');
      default:
        return '';
    }
  },

  getUrlIcon: (): LucideIcon => Link,

  getUrlDisplayName: (url: string): string => {
    try {
      const urlObj = new URL(url);
      return urlObj.hostname;
    } catch {
      return url;
    }
  },

  /**
   * Pick a single URL to show in the upload-progress bubble when the user
   * submitted a list (Multiple Pages mode). We track the whole batch as one
   * file now — see `addUrlsToUploadStore` in useImportUrl — so we need one
   * string that represents the batch: the shared hostname when they all
   * come from the same site, otherwise the first URL (full path) plus a
   * "+N" suffix so the bubble doesn't silently hide the count. The first
   * URL wins (not just its hostname) so a batch like
   * `[quickchat.ai/pricing, onet.pl, wp.pl]` shows `quickchat.ai/pricing +2`
   * rather than the hostname alone — the path is the recognizable bit.
   *
   * The FileItem component splits this string on ` +N` to render the URL
   * and the badge separately, so a long URL can truncate while the count
   * stays visible.
   */
  getRepresentativeUrl: (urls: string[]): string => {
    if (urls.length === 0) return '';
    if (urls.length === 1) return urls[0];
    try {
      const hostnames = urls.map((u) => new URL(u).hostname);
      const unique = new Set(hostnames);
      if (unique.size === 1) {
        return urls[0];
      }
      return `${urls[0]} +${urls.length - 1}`;
    } catch {
      return urls[0];
    }
  },
};

export const {
  validateUrl,
  parseMultipleUrls,
  formatUrl,
  getModalIdByType,
  isSingleUrlType,
  getTitle,
  getDescription,
  getUrlLabel,
  getUrlIcon,
  getUrlDisplayName,
  getRepresentativeUrl,
} = UploadUrlModalHelper;
